import { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Alert,
  Stack,
  Grid,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper
} from '@mui/material';
import { Warning, Payment, CheckCircle, CreditCard } from '@mui/icons-material';
import dayjs from 'dayjs';
import { updateSubscription } from 'api/subscription';
import { useSubscription } from 'contexts/SubscriptionContext';
import { useSubStores } from 'contexts/SubStoreContext';
import { createPaymentIntent, confirmPayment } from 'api/payment';
import { loadStripe } from '@stripe/stripe-js';
import StripePaymentForm from 'components/StripePaymentForm';

export default function ManageSubscription() {
  const { subscription: sub, plans, loading, updateSubscription, refreshSubscription } = useSubscription();
  const { substores } = useSubStores();
  const [planSelectionDialogOpen, setPlanSelectionDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [planSpan, setPlanSpan] = useState(''); // 'monthly' or 'yearly'
  const [stripe, setStripe] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [paymentResult, setPaymentResult] = useState(null); // 'success' or 'error'
  const [paymentMessage, setPaymentMessage] = useState('');

  // Initialize Stripe
  useEffect(() => {
    const initializeStripe = async () => {
      const stripeInstance = await loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY); // Replace with your actual Stripe publishable key
      setStripe(stripeInstance);
    };
    initializeStripe();
  }, []);

  // Remove the old loadData function since we're using context now

  // Calculate days until expiration
  const getDaysUntilExpiration = () => {
    if (!sub?.end_date) return null;
    const endDate = dayjs(sub.end_date);
    const today = dayjs();
    return endDate.diff(today, 'day');
  };

  // Check if subscription expires in 7 days or less
  const isExpiringSoon = () => {
    const daysLeft = getDaysUntilExpiration();
    return daysLeft !== null && daysLeft <= 7 && daysLeft >= 0;
  };

  // Get due amount based on current plan
  const getDueAmount = () => {
    if (!sub?.plan) return null;
    return planSpan === 'monthly' ? sub.plan.monthly_price : sub.plan.yearly_price;
  };

  const handlePaymentSuccess = async (paymentIntent) => {
    if (!selectedPlan || !paymentIntent?.id) {
      setError('Please select a plan.');
      return;
    }

    setPaymentLoading(true);
    setError('');

    try {
      // Confirm payment on backend using the confirmed PaymentIntent id
      await confirmPayment(paymentIntent.id);
      
      // Reload subscription data
      await refreshSubscription();
      
      // Show success message and close dialogs
      setPaymentResult('success');
      setPaymentMessage(`Payment successful! Your ${selectedPlan.name} subscription has been activated.`);
      setPaymentDialogOpen(false);
      setPlanSelectionDialogOpen(false);
      setError('');
      
      // Clear success message after 5 seconds
      setTimeout(() => {
        setPaymentResult(null);
        setPaymentMessage('');
      }, 5000);
      
    } catch (err) {
      setPaymentResult('error');
      setPaymentMessage(err?.response?.data?.error || 'Payment failed. Please try again.');
      setError(err?.response?.data?.error || 'Payment failed. Please try again.');
      
      // Clear error message after 5 seconds
      setTimeout(() => {
        setPaymentResult(null);
        setPaymentMessage('');
      }, 5000);
    } finally {
      setPaymentLoading(false);
    }
  };

  const handlePaymentError = (errorMessage) => {
    setPaymentResult('error');
    setPaymentMessage(errorMessage);
    setError(errorMessage);
    
    // Clear error message after 5 seconds
    setTimeout(() => {
      setPaymentResult(null);
      setPaymentMessage('');
    }, 5000);
  };

  const handlePlanSelection = (plan) => {
    setSelectedPlan(plan);
    setPlanSelectionDialogOpen(false);
    setPaymentDialogOpen(true);
  };

  const handleClosePaymentDialog = () => {
    setPaymentDialogOpen(false);
    setPaymentResult(null);
    setPaymentMessage('');
    setError('');
  };

  const handleClosePlanSelectionDialog = () => {
    setPlanSelectionDialogOpen(false);
    setPaymentResult(null);
    setPaymentMessage('');
    setError('');
  };

  // Check if user can downgrade to a specific plan
  const canDowngradeToPlan = (newPlan) => {
    if (!sub?.plan || !newPlan) return true;
    
    const currentPlanName = sub.plan.name.toLowerCase();
    const newPlanName = newPlan.name.toLowerCase();
    
    // If downgrading from Pro to Starter, check substore count
    if (currentPlanName === 'pro' && newPlanName === 'starter') {
      const substoreCount = substores.length;
      if (substoreCount > 5) {
        return {
          canDowngrade: false,
          message: `Cannot downgrade to Starter plan. You currently have ${substoreCount} substores, but Starter plan allows maximum 5 substores. Please delete ${substoreCount - 5} substore(s) before downgrading.`
        };
      }
    }
    
    return { canDowngrade: true };
  };

  const handleRenewChangePlan = () => {
    setPlanSelectionDialogOpen(true);
  };

  const handleProceedToPayment = () => {
    if (!sub?.plan) {
      setError('No current plan found.');
      return;
    }
    setSelectedPlan(sub.plan);
    setPlanSpan(sub.plan_span);
    setPaymentDialogOpen(true);
  };

  // When payment dialog opens with a selected plan/span, create a PaymentIntent and store clientSecret
  useEffect(() => {
    const preparePayment = async () => {
      if (!paymentDialogOpen || !selectedPlan) return;
      setPaymentLoading(true);
      setError('');
      setClientSecret(null);
      try {
        const paymentData = await createPaymentIntent(selectedPlan.id, planSpan);
        setClientSecret(paymentData.client_secret);
      } catch (err) {
        setError(err?.response?.data?.error || 'Failed to initialize payment.');
      } finally {
        setPaymentLoading(false);
      }
    };
    preparePayment();
  }, [paymentDialogOpen, selectedPlan, planSpan]);

  const planName = sub?.plan?.name ?? 'â€”';
  const startDate = sub?.start_date ? dayjs(sub.start_date).format('YYYY-MM-DD') : 'â€”';
  const endDate = sub?.end_date ? dayjs(sub.end_date).format('YYYY-MM-DD') : 'â€”';
  const isActive = sub?.is_active ? 'Active' : 'Inactive';
  const daysLeft = getDaysUntilExpiration();
  const expiringSoon = isExpiringSoon();
  const dueAmount = getDueAmount();

  const handleRefresh = async () => {
    setError('');
    try {
      await refreshSubscription();
    } catch (error) {
      setError('Failed to refresh subscription details');
    }
  };

  return (
    <Box p={3} maxWidth={800}>
      <Typography variant="h3" mb={3}>Manage Subscription</Typography>

      {loading ? (
        <Stack alignItems="center" mt={6}><CircularProgress /></Stack>
      ) : (
        <>
          {!!error && (
            <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
          )}

          {/* Payment Result Messages */}
          {paymentResult === 'success' && (
            <Alert 
              severity="success" 
              sx={{ mb: 3 }}
              icon={<CheckCircle />}
              onClose={() => {
                setPaymentResult(null);
                setPaymentMessage('');
              }}
            >
              {paymentMessage}
            </Alert>
          )}

          {paymentResult === 'error' && (
            <Alert 
              severity="error" 
              sx={{ mb: 3 }}
              onClose={() => {
                setPaymentResult(null);
                setPaymentMessage('');
              }}
            >
              {paymentMessage}
            </Alert>
          )}

          {/* Main Subscription Card */}
          <Card sx={{ mb: 4 }}>
            <CardHeader title="Subscription Details" />
            <CardContent>
              <Stack spacing={3}>
                {/* Current Plan Section */}
                <Box>
                  <Typography variant="h5" gutterBottom>Current Plan</Typography>
                  <Typography variant="h4" color="primary" gutterBottom>
                    {planName} ({sub?.plan_span === 'yearly' ? 'Yearly' : 'Monthly'})
                  </Typography>
                  <Chip 
                    label={isActive} 
                    color={isActive === 'Active' ? 'success' : 'error'}
                    size="small"
                    sx={{ mb: 2 }}
                  />
                </Box>

                <Divider />

                {/* Dates Section */}
                <Box>
                  <Typography variant="h5" gutterBottom>Subscription Dates</Typography>
                  <Stack spacing={1}>
                    <Typography variant="h6">
                      <strong>Start Date:</strong> {startDate}
                    </Typography>
                    <Typography variant="h6">
                      <strong>End Date:</strong> {endDate}
                    </Typography>
                    {daysLeft !== null && (
                      <Typography variant="h6" color={expiringSoon ? 'warning.main' : 'text.secondary'}>
                        <strong>Days Remaining:</strong> {daysLeft}
                      </Typography>
                    )}
                  </Stack>
                </Box>

                <Divider />

                {/* Renew/Change Plan Button */}
                <Box>
                  <Button 
                    variant="contained" 
                    color="primary"
                    size="large"
                    onClick={handleRenewChangePlan}
                    startIcon={<Payment />}
                    sx={{ mb: 2 }}
                  >
                    Renew/Change Plan
                  </Button>
                </Box>

                <Divider />

                {/* Due Amount Section */}
                <Box>

                  
                  {expiringSoon && (
                    <Alert 
                      severity="warning" 
                      icon={<Warning />}
                      sx={{ mb: 2 }}
                    >
                      <Typography variant="body1">
                        Your subscription expires in {daysLeft} day{daysLeft !== 1 ? 's' : ''}. 
                        Please renew to avoid service interruption.
                      </Typography>
                    </Alert>
                  )}

        
              
                </Box>
              </Stack>
            </CardContent>
          </Card>

          {/* Plan Selection Dialog */}
          <Dialog open={planSelectionDialogOpen} onClose={handleClosePlanSelectionDialog} fullWidth maxWidth="md">
            <DialogTitle>Available Plan</DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={3}>
                {plans.flatMap((plan) => ([
                  {
                    plan,
                    span: 'monthly',
                    label: `${plan.name} Monthly`,
                    price: plan.monthly_price,
                  },
                  {
                    plan,
                    span: 'yearly',
                    label: `${plan.name} Yearly`,
                    price: plan.yearly_price,
                  }
                ])).map(({ plan, span, label, price }) => {
                  const isCurrent = sub?.plan?.id === plan.id && (sub?.plan_span || 'monthly') === span;
                  const isSelected = selectedPlan?.id === plan.id && planSpan === span;
                  const validation = canDowngradeToPlan(plan);
                  const isRestricted = !validation.canDowngrade;
                  return (
                    <Grid item xs={12} md={6} key={plan.id + '-' + span}>
                      <Paper
                        elevation={isCurrent ? 4 : 1}
                        sx={{
                          p: 3,
                          border: isCurrent ? '3px solid #2ecc40' : 
                                 isRestricted ? '2px solid #f44336' : 
                                 isSelected ? '2px solid #1971c2' : '1px solid #ccc',
                          background: isCurrent ? '#f6fff6' : 
                                    isRestricted ? '#ffebee' : 
                                    isSelected ? '#e3f2fd' : undefined,
                          cursor: isRestricted ? 'not-allowed' : 'pointer',
                          transition: 'border 0.2s',
                          opacity: isRestricted ? 0.7 : 1
                        }}
                        onClick={() => {
                          if (!isRestricted) {
                            setSelectedPlan(plan);
                            setPlanSpan(span);
                          }
                        }}
                      >
                        <Stack spacing={2}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="h5">{label}</Typography>
                            {isCurrent && <CheckCircle color="success" />}
                            {isRestricted && <Warning color="error" />}
                          </Box>
                          {isRestricted && (
                            <Alert severity="error" sx={{ mt: 1 }}>
                              <Typography variant="body2">
                                {validation.message}
                              </Typography>
                            </Alert>
                          )}
                          <Typography variant="h4" color="primary">${price}</Typography>
                          {Array.isArray(plan.features) && plan.features.length > 0 && (
                            <Box>
                              <Typography variant="subtitle1" gutterBottom>Features:</Typography>
                              <List dense>
                                {plan.features.map((feature, idx) => (
                                  <ListItem key={idx} sx={{ py: 0 }}>
                                    <ListItemText primary={feature} slotProps={{ primary: { variant: "body2" }}} />
                                  </ListItem>
                                ))}
                              </List>
                            </Box>
                          )}
                        </Stack>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClosePlanSelectionDialog}>
                Cancel
              </Button>
              <Button
                onClick={() => {
                  // Validate if user can downgrade to selected plan
                  const validation = canDowngradeToPlan(selectedPlan);
                  if (!validation.canDowngrade) {
                    setError(validation.message);
                    return;
                  }
                  
                  setPlanSelectionDialogOpen(false);
                  setPaymentDialogOpen(true);
                }}
                variant="contained"
                disabled={!selectedPlan}
              >
                Select Plan
              </Button>
            </DialogActions>
          </Dialog>

          {/* Payment Summary Dialog */}
          <Dialog open={paymentDialogOpen} onClose={handleClosePaymentDialog} fullWidth maxWidth="sm">
            <DialogTitle>Payment Summary</DialogTitle>
            <DialogContent dividers>
              <Stack spacing={3}>
                {/* Payment Summary */}
                <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="h5" gutterBottom>Order Summary</Typography>
                  <Typography variant="h6">
                    <strong>Plan Name:</strong> {selectedPlan?.name}
                  </Typography>
                  <Typography variant="h6">
                    <strong>Duration:</strong> {planSpan === 'monthly' ? 'Monthly' : 'Yearly'}
                  </Typography>
                  <Typography variant="h5" color="primary">
                    <strong>Amount:</strong> ${planSpan === 'monthly' ? selectedPlan?.monthly_price : selectedPlan?.yearly_price}
                  </Typography>
                </Box>

                {/* Stripe Payment Form */}
                {selectedPlan && clientSecret && (
                  <StripePaymentForm
                    amount={planSpan === 'monthly' ? selectedPlan.monthly_price : selectedPlan.yearly_price}
                    clientSecret={clientSecret}
                    onSuccess={handlePaymentSuccess}
                    onError={handlePaymentError}
                    loading={paymentLoading}
                  />
                )}
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClosePaymentDialog} disabled={paymentLoading}>
                Cancel
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Box>
  );
}